import { isValidDate } from '@fullcalendar/core/internal';
import { ChevronDown } from '@griply/assets';
import Typography from '@mui/material/Typography';
import { addDays, format, isSameDay } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DatepickerWithOptions,
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { Icon } from 'shared/components/ui/icon';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './datepicker-select.style';

export type DatepickerSelectProps = {
  value: Timestamp | undefined | null;
  onChange: (date?: Timestamp) => void;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  placeholder?: string;
  datePresets?: DatePresets[];
  canRemoveValue?: boolean;
  isFullWidth?: boolean;
};

export const DatepickerSelect: React.FC<DatepickerSelectProps> = ({
  value,
  onChange,
  dateFormat,
  weekStartsOn,
  placeholder,
  datePresets = [],
  canRemoveValue,
  isFullWidth,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { t } = useTranslation();
  const today = useToday();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const _onChange = (date?: Timestamp) => {
    onChange(date);
    closeMenu();
  };

  const label = useMemo(() => {
    if (!value || !isValidDate(value)) {
      return placeholder;
    }

    if (isSameDay(value, today)) {
      return t('general.labels.dates.today');
    }

    if (isSameDay(value, addDays(today, 1))) {
      return t('general.labels.dates.tomorrow');
    }

    if (isSameDay(value, addDays(today, -1))) {
      return t('general.labels.dates.yesterday');
    }

    return format(value, 'd MMM');
  }, [value, today, placeholder, t]);

  return (
    <>
      <Styled.SelectContainer
        ref={buttonRef}
        onClick={openMenu}
        $isFullWidth={!!isFullWidth}
        type="button"
      >
        <Typography variant="button">{label}</Typography>
        <Styled.IconContainer $open={menuOpen}>
          <Icon icon={ChevronDown} size={16} strokeWidth={2} />
        </Styled.IconContainer>
      </Styled.SelectContainer>

      {menuOpen && (
        <DatepickerWithOptions
          referenceElement={buttonRef}
          value={value ?? undefined}
          onChange={_onChange}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          icon={IconOptions.Calendar}
          onOutside={closeMenu}
          datePresets={datePresets}
          canRemoveValue={!!canRemoveValue}
        />
      )}
    </>
  );
};
